// Minimal CFDI 4.0 parser. Walks the DOMParser tree and pulls the fields
// our viewer needs into a plain object — no validation, no SAT-rule
// enforcement (that lives in PdfBuild/Roa). The point is to surface the
// XML's content in a readable shape; if a field is missing the viewer just
// shows "—" for it.
//
// Why DOM parsing (not regex): CFDIs vary in namespace prefix order +
// attribute ordering between PACs, but the structural shape is fixed. DOM
// gives us robust attribute lookup; regex would silently break on the
// first PAC variant that ships with a different namespace alias.

export interface CFDIConcepto {
  cantidad: string;
  unidad: string;
  claveProdServ: string;
  noIdentificacion: string;
  descripcion: string;
  valorUnitario: number;
  importe: number;
  descuento: number;
}

export interface CFDIRelated {
  uuid: string;
  tipoRelacion: string;
}

export interface CFDISummary {
  /** Raw XML, kept for the "Ver XML crudo" accordion + the download button. */
  raw: string;
  /** Comprobante root attributes — every field optional because PACs vary. */
  version: string;
  serie: string;
  folio: string;
  fecha: string;             // ISO 8601 from the XML, formatter's problem
  formaPago: string;
  metodoPago: string;
  moneda: string;
  tipoCambio: string;
  tipoDeComprobante: string; // I (Ingreso) | E (Egreso) | T (Traslado) | N (Nomina) | P (Pago)
  subTotal: number;
  descuento: number;
  total: number;
  exportacion: string;
  lugarExpedicion: string;

  emisor: {
    rfc: string;
    nombre: string;
    regimenFiscal: string;
  };
  receptor: {
    rfc: string;
    nombre: string;
    domicilioFiscalReceptor: string;
    regimenFiscalReceptor: string;
    usoCFDI: string;
  };

  conceptos: CFDIConcepto[];

  totalImpuestosTrasladados: number;
  totalImpuestosRetenidos: number;

  /** TimbreFiscalDigital complement — only present if the doc was stamped. */
  uuid: string;
  fechaTimbrado: string;
  selloCFD: string;          // long base64 string; viewer truncates
  selloSAT: string;
  noCertificadoSAT: string;
  noCertificado: string;

  relacionados: CFDIRelated[];
}

// Returns a fresh empty summary on every call. Critical: previously a
// const with nested arrays + spread copy caused every parse to share the
// same conceptos[] / relacionados[] references — the second invocation
// would inherit the first one's data.
function emptySummary(raw: string): CFDISummary {
  return {
    raw,
    version: '', serie: '', folio: '', fecha: '',
    formaPago: '', metodoPago: '', moneda: '', tipoCambio: '',
    tipoDeComprobante: '', subTotal: 0, descuento: 0, total: 0,
    exportacion: '', lugarExpedicion: '',
    emisor: { rfc: '', nombre: '', regimenFiscal: '' },
    receptor: {
      rfc: '', nombre: '', domicilioFiscalReceptor: '',
      regimenFiscalReceptor: '', usoCFDI: '',
    },
    conceptos: [],
    totalImpuestosTrasladados: 0,
    totalImpuestosRetenidos: 0,
    uuid: '', fechaTimbrado: '', selloCFD: '', selloSAT: '',
    noCertificadoSAT: '', noCertificado: '',
    relacionados: [],
  };
}

export function parseCFDI(xml: string): CFDISummary {
  const doc = new DOMParser().parseFromString(xml, 'text/xml');
  const err = doc.querySelector('parsererror');
  if (err) {
    return emptySummary(xml);
  }
  // querySelector ignores namespaces in modern browsers when given a
  // namespace-prefixed name like "cfdi:Comprobante", so we use getElementsByTagNameNS
  // with '*' to match any namespace + local-name. CFDI uses a few namespaces
  // (cfdi, tfd, pago20, etc.) that PACs alias differently.
  const compEls = doc.getElementsByTagNameNS('*', 'Comprobante');
  const comp = compEls[0];
  if (!comp) return emptySummary(xml);

  const out: CFDISummary = emptySummary(xml);
  out.version          = attr(comp, 'Version');
  out.serie            = attr(comp, 'Serie');
  out.folio            = attr(comp, 'Folio');
  out.fecha            = attr(comp, 'Fecha');
  out.formaPago        = attr(comp, 'FormaPago');
  out.metodoPago       = attr(comp, 'MetodoPago');
  out.moneda           = attr(comp, 'Moneda');
  out.tipoCambio       = attr(comp, 'TipoCambio');
  out.tipoDeComprobante = attr(comp, 'TipoDeComprobante');
  out.subTotal         = numAttr(comp, 'SubTotal');
  out.descuento        = numAttr(comp, 'Descuento');
  out.total            = numAttr(comp, 'Total');
  out.exportacion      = attr(comp, 'Exportacion');
  out.lugarExpedicion  = attr(comp, 'LugarExpedicion');
  out.noCertificado    = attr(comp, 'NoCertificado');

  const emisor = doc.getElementsByTagNameNS('*', 'Emisor')[0];
  if (emisor) {
    out.emisor.rfc = attr(emisor, 'Rfc');
    out.emisor.nombre = attr(emisor, 'Nombre');
    out.emisor.regimenFiscal = attr(emisor, 'RegimenFiscal');
  }
  const receptor = doc.getElementsByTagNameNS('*', 'Receptor')[0];
  if (receptor) {
    out.receptor.rfc = attr(receptor, 'Rfc');
    out.receptor.nombre = attr(receptor, 'Nombre');
    out.receptor.domicilioFiscalReceptor = attr(receptor, 'DomicilioFiscalReceptor');
    out.receptor.regimenFiscalReceptor = attr(receptor, 'RegimenFiscalReceptor');
    out.receptor.usoCFDI = attr(receptor, 'UsoCFDI');
  }

  const conceptoEls = doc.getElementsByTagNameNS('*', 'Concepto');
  for (let i = 0; i < conceptoEls.length; i++) {
    const c = conceptoEls[i];
    out.conceptos.push({
      cantidad:        attr(c, 'Cantidad'),
      unidad:          attr(c, 'Unidad'),
      claveProdServ:   attr(c, 'ClaveProdServ'),
      noIdentificacion: attr(c, 'NoIdentificacion'),
      descripcion:     attr(c, 'Descripcion'),
      valorUnitario:   numAttr(c, 'ValorUnitario'),
      importe:         numAttr(c, 'Importe'),
      descuento:       numAttr(c, 'Descuento'),
    });
  }

  // Top-level Impuestos rollup (not the per-Concepto one; we only need the
  // header totals for display).
  const impuestosEls = doc.getElementsByTagNameNS('*', 'Impuestos');
  for (let i = 0; i < impuestosEls.length; i++) {
    const im = impuestosEls[i];
    // The top-level Impuestos is the direct child of Comprobante; per-Concepto
    // ones are nested under Concepto. Identify by parent.
    if (im.parentElement && im.parentElement.localName === 'Comprobante') {
      out.totalImpuestosTrasladados = numAttr(im, 'TotalImpuestosTrasladados');
      out.totalImpuestosRetenidos   = numAttr(im, 'TotalImpuestosRetenidos');
      break;
    }
  }

  const tfd = doc.getElementsByTagNameNS('*', 'TimbreFiscalDigital')[0];
  if (tfd) {
    out.uuid             = attr(tfd, 'UUID');
    out.fechaTimbrado    = attr(tfd, 'FechaTimbrado');
    out.selloCFD         = attr(tfd, 'SelloCFD');
    out.selloSAT         = attr(tfd, 'SelloSAT');
    out.noCertificadoSAT = attr(tfd, 'NoCertificadoSAT');
  }

  const relatedEls = doc.getElementsByTagNameNS('*', 'CfdiRelacionado');
  // tipoRelacion lives on the parent CfdiRelacionados node — same value for
  // every CfdiRelacionado inside it. Walk parent → children.
  const relParents = doc.getElementsByTagNameNS('*', 'CfdiRelacionados');
  for (let i = 0; i < relParents.length; i++) {
    const tipo = attr(relParents[i], 'TipoRelacion');
    const kids = relParents[i].getElementsByTagNameNS('*', 'CfdiRelacionado');
    for (let j = 0; j < kids.length; j++) {
      out.relacionados.push({ uuid: attr(kids[j], 'UUID'), tipoRelacion: tipo });
    }
  }
  // Fallback path for CFDIs where the relationship wasn't structured under a
  // CfdiRelacionados parent (older v3.3 docs); just dump uuids w/o tipo.
  if (out.relacionados.length === 0 && relatedEls.length > 0) {
    for (let i = 0; i < relatedEls.length; i++) {
      out.relacionados.push({ uuid: attr(relatedEls[i], 'UUID'), tipoRelacion: '' });
    }
  }

  return out;
}

function attr(el: Element, name: string): string {
  // CFDI attributes are unprefixed local names so getAttribute is correct.
  // We try both casings just in case some PAC ships lowercase.
  return el.getAttribute(name) ?? el.getAttribute(name.toLowerCase()) ?? '';
}

function numAttr(el: Element, name: string): number {
  const s = attr(el, name);
  if (!s) return 0;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
}

// Human-readable label for the CFDI tipo code (matches SAT catalog c_TipoDeComprobante).
export function tipoLabel(code: string): string {
  switch (code) {
    case 'I': return 'Ingreso';
    case 'E': return 'Egreso';
    case 'T': return 'Traslado';
    case 'N': return 'Nómina';
    case 'P': return 'Pago';
    default:  return code || '—';
  }
}

// Color suggestion for the tipo badge. Maps onto Tremor's Badge palette.
export function tipoColor(code: string): 'green' | 'amber' | 'blue' | 'slate' | 'violet' {
  switch (code) {
    case 'I': return 'green';
    case 'E': return 'amber';
    case 'P': return 'blue';
    case 'N': return 'violet';
    default:  return 'slate';
  }
}

// Catalogs we recognize for friendlier inline labels. Extend as needed —
// unknown codes pass through unchanged so nothing breaks if SAT publishes
// a new value before we update.
const FORMA_PAGO: Record<string, string> = {
  '01': 'Efectivo',
  '02': 'Cheque nominativo',
  '03': 'Transferencia',
  '04': 'Tarjeta de crédito',
  '28': 'Tarjeta de débito',
  '99': 'Por definir',
};
const METODO_PAGO: Record<string, string> = {
  PUE: 'Pago en una sola exhibición',
  PPD: 'Pago en parcialidades o diferido',
};
const USO_CFDI: Record<string, string> = {
  G01: 'Adquisición de mercancías',
  G02: 'Devoluciones, descuentos o bonificaciones',
  G03: 'Gastos en general',
  P01: 'Por definir',
  S01: 'Sin efectos fiscales',
  CP01: 'Pagos',
};

export function formaPagoLabel(code: string): string {
  return code ? (FORMA_PAGO[code] ? code + ' — ' + FORMA_PAGO[code] : code) : '—';
}
export function metodoPagoLabel(code: string): string {
  return code ? (METODO_PAGO[code] ? code + ' — ' + METODO_PAGO[code] : code) : '—';
}
export function usoCFDILabel(code: string): string {
  return code ? (USO_CFDI[code] ? code + ' — ' + USO_CFDI[code] : code) : '—';
}

// Pretty-print + (very lightly) syntax-highlight an XML string. Returns
// safe HTML — DOMParser already validated; tag/attr names are XML chars.
// Use dangerouslySetInnerHTML in the consumer.
export function highlightXml(xml: string): string {
  const escaped = xml
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return escaped
    // attribute names + values: name="value"
    .replace(/(\b[\w-]+:)?([\w-]+)(=)(&quot;[^&]*?&quot;|"[^"]*?")/g,
      (_, ns, name, eq, val) => {
        const nsHtml = ns ? '<span class="text-amber-700 dark:text-amber-400">' + ns + '</span>' : '';
        return nsHtml
          + '<span class="text-violet-700 dark:text-violet-400">' + name + '</span>'
          + '<span class="text-tremor-content">' + eq + '</span>'
          + '<span class="text-emerald-700 dark:text-emerald-400">' + val + '</span>';
      })
    // tag names: <name  or  </name  or  <ns:name
    .replace(/(&lt;\/?)([\w-]+:)?([\w-]+)/g,
      (_, lt, ns, name) => {
        const nsHtml = ns ? '<span class="text-amber-700 dark:text-amber-400">' + ns + '</span>' : '';
        return lt + nsHtml + '<span class="text-sky-700 dark:text-sky-400 font-medium">' + name + '</span>';
      });
}

// Pretty-print XML with line breaks + indentation so long sello strings
// don't render as a single 4000-char line. Cheap state machine, no XSLT.
export function prettyXml(xml: string): string {
  let out = '';
  let indent = 0;
  let i = 0;
  while (i < xml.length) {
    if (xml[i] === '<') {
      // <?xml...?>, <!--...-->, </…>, <…/>, or <…>
      if (xml.startsWith('<?', i) || xml.startsWith('<!--', i)) {
        const close = xml.indexOf(xml.startsWith('<?', i) ? '?>' : '-->', i);
        const end = close < 0 ? xml.length : close + 2;
        out += xml.slice(i, end) + '\n';
        i = end;
        continue;
      }
      const close = xml.indexOf('>', i);
      if (close < 0) { out += xml.slice(i); break; }
      const tag = xml.slice(i, close + 1);
      const isClose = tag.startsWith('</');
      const isSelf = tag.endsWith('/>');
      if (isClose) indent = Math.max(0, indent - 1);
      out += '  '.repeat(indent) + tag + '\n';
      if (!isClose && !isSelf) indent++;
      i = close + 1;
    } else {
      // text between tags — emit only if non-whitespace
      const next = xml.indexOf('<', i);
      const chunk = xml.slice(i, next < 0 ? xml.length : next).trim();
      if (chunk) out += '  '.repeat(indent) + chunk + '\n';
      if (next < 0) break;
      i = next;
    }
  }
  return out;
}
